import { customElement } from '@fmr-ap142296/brick-helpers';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __decorate(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
  var e = new Error(message);
  return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const i=(i,e)=>"method"===e.kind&&e.descriptor&&!("value"in e.descriptor)?{...e,finisher(n){n.createProperty(e.key,i);}}:{kind:"field",key:Symbol(),placement:"own",descriptor:{},originalKey:e.key,initializer(){"function"==typeof e.initializer&&(this[e.key]=e.initializer.call(this));},finisher(n){n.createProperty(e.key,i);}},e=(i,e,n)=>{e.constructor.createProperty(n,i);};function n$1(n){return (t,o)=>void 0!==o?e(n,t,o):i(n,t)}

/**
 * @license
 * Copyright 2021 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */var n;null!=(null===(n=window.HTMLSlotElement)||void 0===n?void 0:n.prototype.assignedElements)?(o,n)=>o.assignedElements(n):(o,n)=>o.assignedNodes(n).filter((o=>o.nodeType===Node.ELEMENT_NODE));

function apexCustomElement(options) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function apexCustomElementMixin(constructor) {
        const CustomElementMixin = customElement({
            namespace: options.namespace,
            shadowDOM: options.shadowDOM,
            name: options.name
        });
        class ClassInTransition extends CustomElementMixin(constructor) {
            static defineCustomElement(namespace = options.namespace, pvdNamespace = options.pvdNamespace) {
                this.namespace = namespace;
                this.pvdNamespace = pvdNamespace;
                const customElementName = `${namespace}-${options.name}`;
                if (!customElements.get(customElementName)) {
                    customElements.define(customElementName, this);
                }
            }
            get idPrefix() {
                const now = new Date().getTime();
                let attrVal = this.getAttribute('id-prefix');
                if (!attrVal) {
                    attrVal = `${options.name}-${Math.floor(Math.random() * now)}`;
                }
                return attrVal;
            }
            set idPrefix(newVal) {
                this.setAttribute('id-prefix', newVal);
            }
        }
        __decorate([
            n$1({ type: String, attribute: 'id-prefix', reflect: true }),
            __metadata("design:type", String),
            __metadata("design:paramtypes", [String])
        ], ClassInTransition.prototype, "idPrefix", null);
        const existingConnectedCallback = constructor.prototype.connectedCallback;
        Object.defineProperty(constructor.prototype, 'connectedCallback', {
            value() {
                if (existingConnectedCallback) {
                    existingConnectedCallback.apply(this);
                }
                if (!this.hasAttribute('id-prefix')) {
                    // We have the code generate one so there is always a value. If there isn't one on the attribute, we'll add it here
                    // eslint-disable-next-line no-self-assign
                    this.idPrefix = this.idPrefix;
                }
            },
            configurable: true
        });
        return ClassInTransition;
    };
}

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
/**
 * Returns the configurable factory for deprecating a property
 *
 * @param { newPropertyKey }: { newPropertyKey: string }
 * @return (get: () => unknown, set: (newValue: unknown) => void, propertyDescriptor: PropertyDescriptor) => PropertyDescriptor
 */
function deprecatePropertyTo({ newPropertyKey }) {
    /**
     * Marks a property as deprecated and automatically assigns it to the given new property.
     *
     * @param target: unknown - the property being targeted
     * @param propertyKey: string - the property name
     * @param propertyDescriptor: PropertyDescriptor - the property description object that stores getter, setter and other info
     * @returns { get: () => unknown, set: (newValue: unknown) => void, configurable: boolean}
     */
    // eslint-disable-next-line func-names
    return function (target, propertyKey, propertyDescriptor) {
        const { get, set } = propertyDescriptor;
        function getter() {
            // @ts-ignore
            const self = this;
            if (get) {
                return get.call(self);
            }
            return self[newPropertyKey];
        }
        function setter(newValue) {
            // @ts-ignore
            const self = this;
            /* istanbul ignore next */ // we don't need to test what happens when console is undefined (rare case)
            if (console === null || console === void 0 ? void 0 : console.warn) {
                console.warn(`"${propertyKey}" has been deprecated - use "${newPropertyKey}" instead!`);
            }
            if (set) {
                set.call(self, newValue);
                return;
            }
            const oldValue = getter.call(self);
            self[newPropertyKey] = newValue;
            self.requestUpdate(propertyKey, oldValue);
        }
        return {
            get: getter,
            set: setter,
            configurable: true
        };
    };
}

/* eslint-disable no-console */
/**
 * Returns the configurable factory for deprecating a property for removal
 *
 * @param { removalVersion, details }: { removalVersion: string; details?: string; }
 * @returns (target: unknown, propertyName: string) => void
 */
function deprecatePropertyForRemoval({ removalVersion, details }) {
    /**
     * Outputs a message to the console that the property is deprecated and will be removed
     * in the specified version.
     * If provided, more details will also be warned to the console.
     *
     * @param target: unknown - the instance or class that the property belongs to
     * @param propertyName: string - the name of the property to be deprecated
     */
    // eslint-disable-next-line func-names
    return function (target, propertyName) {
        /* istanbul ignore next */ // we don't need to test what happens when console is undefined (rare case)
        if (console === null || console === void 0 ? void 0 : console.warn) {
            // we pass an empty string for details if it's undefined so that the word "undefined" does not print to the console
            console.warn(`Property "${propertyName}" will be removed in version ${removalVersion}!`, details || '');
        }
    };
}

/* eslint-disable @typescript-eslint/no-explicit-any */
class ApexEventBus {
    constructor() {
        this.bus = document.createElement('div');
    }
    addEventListener(event, callback) {
        this.bus.addEventListener(event, callback, true);
    }
    removeEventListener(event, callback) {
        this.bus.removeEventListener(event, callback, true);
    }
    dispatchEvent(event, detail = {}) {
        this.bus.dispatchEvent(new CustomEvent(event, { detail }));
    }
}
// @ts-ignore
window.ApexEventBus = window.ApexEventBus || new ApexEventBus();

class ApexFormatters {
    static ssn(inputSsn) {
        // ensure we're making a copy and not mutating
        let formattedSsn = `${inputSsn}`;
        /* istanbul ignore else */
        if (formattedSsn.length) {
            formattedSsn = formattedSsn.toString().replace(/\D/g, '');
        }
        const len = formattedSsn.length;
        /* istanbul ignore else */
        if (len < 4) {
            return formattedSsn;
        }
        else if (len < 6) {
            formattedSsn = `${formattedSsn.substr(0, 3)}-${formattedSsn.substr(3)}`;
        }
        else if (len > 5) {
            formattedSsn = `${formattedSsn.substr(0, 3)}-${formattedSsn.substr(3, 2)}-${formattedSsn.substr(5, 5)}`;
        }
        return formattedSsn;
    }
    static phoneNumber(inputPhoneNumber) {
        // ensure we're making a copy and not mutating
        let formattedPhoneNumber = `${inputPhoneNumber}`;
        /* istanbul ignore else */
        if (formattedPhoneNumber.length) {
            formattedPhoneNumber = formattedPhoneNumber.toString().replace(/\D/g, '');
        }
        const len = formattedPhoneNumber.length;
        /* istanbul ignore else */
        if (len < 4) {
            return formattedPhoneNumber;
        }
        else if (len < 7) {
            formattedPhoneNumber = `(${formattedPhoneNumber.substr(0, 3)}) ${formattedPhoneNumber.substr(3)}`;
        }
        else if (len > 6) {
            formattedPhoneNumber = `(${formattedPhoneNumber.substr(0, 3)}) ${formattedPhoneNumber.substr(3, 3)}-${formattedPhoneNumber.substr(6, 4)}`;
        }
        return formattedPhoneNumber;
    }
}

/**
 * This file was pulled from the defunct UOE common content repo
 * Apex Android and iOS both bake these values in so for the meantime, we are too.
 * Old package: @fmr-pr103625/apex-common-content
 * Old repo: https://itec-stash.fmr.com/projects/PR103625/repos/ap137165-uoe-shared-utilities/
 */
const percentAllocation = {
    groupLabel: '',
    fields: {
        accountNumber: {
            name: 'accountNumber',
            label: 'Account Number',
            validationRules: {
                required: false,
                max: 12,
                min: 0,
                regex: '^[_A-z0-9]*((-|s)*[_A-z0-9])*$'
            },
            errorMesssages: {
                isRequired: 'Provide an account number.',
                maxLength: 'Too many characters entered.',
                minLength: 'Too few characters entered.',
                fieldRegex: 'The account number provided is invalid.'
            }
        },
        percentage: {
            name: 'percentage',
            label: 'Percentage',
            validationRules: {
                required: false,
                max: 3,
                min: 0,
                regex: '^(s*|[0-9]|[1-9][0-9]|100)$'
            },
            errorMesssages: {
                isRequired: 'Provide a percentage.',
                maxLength: 'Too many characters entered.',
                minLength: 'Too few characters entered.',
                fieldRegex: 'Percentage must be between 0 and 100.'
            }
        }
    }
};
const apexAddress = {
    groupLabel: 'Legal/Residential Address',
    fields: {
        addressLine1: {
            name: 'addressLine1',
            label: 'Address line 1',
            errorMessages: {
                isRequired: 'Provide an address line 1.',
                maxLength: 'Address line 1 has too many characters.',
                minLength: 'Address line 1 has too few characters.',
                fieldRegex: 'Address line 1 contains invalid characters.',
                poboxError: 'For address line 1, a legal/residential address cannot contain a PO Box or "in care of" (c/o) address. Please enter a valid street address.'
            },
            validationRules: {
                validValues: null,
                isRequired: true,
                minLength: 1,
                maxLength: 30,
                fieldRegex: "^[a-zA-Z0-9#*.'-/ ,();:@&]*$"
            }
        },
        addressLine2: {
            name: 'addressLine2',
            label: 'Address line 2',
            errorMessages: {
                isRequired: 'Provide an address line 2.',
                maxLength: 'Address line 2 has too many characters.',
                minLength: 'Address line 2 has too few characters.',
                fieldRegex: 'Address line 2 contains invalid characters.',
                poboxError: 'For address line 2, a legal/residential address cannot contain a PO Box or "in care of" (c/o) address. Please enter a valid street address.'
            },
            validationRules: {
                validValues: null,
                isRequired: false,
                minLength: 0,
                maxLength: 30,
                fieldRegex: "^[a-zA-Z0-9#*.'-/ ,();:@&]*$"
            }
        },
        addressLine3: {
            name: 'addressLine3',
            label: 'Address line 3',
            errorMessages: {
                isRequired: 'Provide an address line 3.',
                maxLength: 'Address line 3 has too many characters.',
                minLength: 'Address line 3 has too few characters.',
                fieldRegex: 'Address line 3 contains invalid characters.',
                poboxError: 'For address line 3, a legal/residential address cannot contain a PO Box or "in care of " (c/o) address. Please enter a valid street address.'
            }
        },
        cityName: {
            name: 'cityName',
            label: 'City',
            errorMessages: {
                isRequired: 'Provide a city.',
                maxLength: 'City has too many characters.',
                minLength: 'City has too few characters.',
                fieldRegex: 'City contains invalid characters.'
            },
            validationRules: {
                validValues: null,
                isRequired: true,
                minLength: 1,
                maxLength: 30,
                fieldRegex: "^([:;&quot;&apos;&lt;&gt;,.\\/?0-9a-zA-Z `~!'@#$%^&*()-_=+\\[\\]\\{\\}|])*"
            }
        },
        stateCode: {
            name: 'stateCode',
            label: 'State',
            errorMessages: {
                isRequired: 'Select a state.',
                maxLength: 'State has too many characters.',
                minLength: 'State has too few characters.',
                fieldRegex: 'State contains invalid characters.'
            },
            stateCodes: {
                88: 'Unknown',
                AA: 'Armed Forces Americas',
                AE: 'Armed Forces Canada  Overseas',
                AK: 'Alaska',
                AL: 'Alabama',
                AP: 'Armed Forces Pacific',
                AR: 'Arkansas',
                AS: 'American Samoa',
                AZ: 'Arizona',
                CA: 'California',
                CO: 'Colorado',
                CT: 'Connecticut',
                DC: 'District of Columbia',
                DE: 'Delaware',
                FL: 'Florida',
                FM: 'Federated States of Micronesia',
                GA: 'Georgia',
                GU: 'Guam',
                HI: 'Hawaii',
                IA: 'Iowa',
                ID: 'Idaho',
                IL: 'Illinois',
                IN: 'Indiana',
                KS: 'Kansas',
                KY: 'Kentucky',
                LA: 'Louisiana',
                MA: 'Massachusetts',
                MD: 'Maryland',
                ME: 'Maine',
                MH: 'Marshall Islands',
                MI: 'Michigan',
                MN: 'Minnesota',
                MO: 'Missouri',
                MP: 'Northern Mariana Islands',
                MS: 'Mississippi',
                MT: 'Montana',
                NC: 'North Carolina',
                ND: 'North Dakota',
                NE: 'Nebraska',
                NH: 'New Hampshire',
                NJ: 'New Jersey',
                NM: 'New Mexico',
                NV: 'Nevada',
                NY: 'New York',
                OH: 'Ohio',
                OK: 'Oklahoma',
                OR: 'Oregon',
                PA: 'Pennsylvania',
                PR: 'Puerto Rico',
                PW: 'Palau',
                RI: 'Rhode Island',
                SC: 'South Carolina',
                SD: 'South Dakota',
                TN: 'Tennessee',
                TX: 'Texas',
                UT: 'Utah',
                VA: 'Virginia',
                VI: 'Virgin Islands',
                VT: 'Vermont',
                WA: 'Washington',
                WI: 'Wisconsin',
                WV: 'West Virginia',
                WY: 'Wyoming'
            },
            validationRules: {
                validValues: [
                    'AA',
                    'AE',
                    'AK',
                    'AL',
                    'AP',
                    'AR',
                    'AS',
                    'AZ',
                    'CA',
                    'CO',
                    'CT',
                    'DC',
                    'DE',
                    'FL',
                    'FM',
                    'GA',
                    'GU',
                    'HI',
                    'IA',
                    'ID',
                    'IL',
                    'IN',
                    'KS',
                    'KY',
                    'LA',
                    'MA',
                    'MD',
                    'ME',
                    'MH',
                    'MI',
                    'MN',
                    'MO',
                    'MP',
                    'MS',
                    'MT',
                    'NC',
                    'ND',
                    'NE',
                    'NH',
                    'NJ',
                    'NM',
                    'NV',
                    'NY',
                    'OH',
                    'OK',
                    'OR',
                    'PA',
                    'PR',
                    'PW',
                    'RI',
                    'SC',
                    'SD',
                    'TN',
                    'TX',
                    'UT',
                    'VA',
                    'VI',
                    'VT',
                    'WA',
                    'WI',
                    'WV',
                    'WY',
                    '88',
                    '88'
                ],
                isRequired: true,
                minLength: 1,
                maxLength: 2,
                fieldRegex: '.*'
            }
        },
        zipCode: {
            name: 'zipCode',
            label: 'ZIP Code',
            errorMessages: {
                isRequired: 'Provide a ZIP Code.',
                maxLength: 'ZIP Code has too many characters.',
                minLength: 'ZIP Code has too few characters.',
                fieldRegex: 'ZIP Code contains invalid characters.'
            },
            validationRules: {
                validValues: null,
                isRequired: true,
                minLength: 5,
                maxLength: 5,
                fieldRegex: '^[0-9]*$'
            }
        },
        zipCode2: {
            name: 'zipCode2',
            errorMessages: {
                isRequired: 'Provide a ZIP Code extension.',
                maxLength: 'ZIP Code extension has too many characters.',
                minLength: 'ZIP Code extension has too few characters.',
                fieldRegex: 'ZIP Code extension contains invalid characters.'
            }
        },
        province: {
            name: 'province',
            label: 'Province',
            errorMessages: {
                isRequired: 'Provide a province.',
                maxLength: 'Province has too many characters.',
                minLength: 'Province has too few characters.',
                fieldRegex: 'Province contains invalid characters.'
            },
            validationRules: {
                validValues: null,
                isRequired: true,
                minLength: 0,
                maxLength: 20,
                fieldRegex: "^[a-zA-Z0-9.',-]*$"
            }
        },
        postalCode: {
            name: 'postalCode',
            label: 'Postal Code',
            errorMessages: {
                isRequired: 'Provide a Postal Code.',
                maxLength: 'Postal Code has too many characters.',
                minLength: 'Postal Code has too few characters.',
                fieldRegex: 'Postal Code contains invalid characters.'
            },
            validationRules: {
                validValues: null,
                isRequired: true,
                minLength: 0,
                maxLength: 20,
                fieldRegex: "^[a-zA-Z0-9.',\\- ]*$"
            }
        },
        country: {
            name: 'country',
            label: 'Country',
            errorMessages: {
                isRequired: 'Select a country.',
                maxLength: 'Country has too many characters.',
                minLength: 'Country has too few characters.',
                fieldRegex: 'Country contains invalid characters.'
            },
            countries: { 999: 'United States' },
            validationRules: {
                validValues: null,
                isRequired: true,
                minLength: 1,
                maxLength: 3,
                fieldRegex: '^[0-9]*$'
            }
        },
        undefined: {}
    }
};
const apexAutocomplete = {
    fields: {
        autocomplete: {
            name: 'autocomplete',
            label: 'Auto complete',
            errorMessages: {
                isRequired: 'Please provide a value.',
                minLength: 'Must type a minimum of 4 characters for suggestions.'
            }
        }
    }
};
const apexDate = {
    groupLabel: 'Date',
    fields: {
        monthSelect: {
            name: 'monthSelect',
            label: 'Month',
            validationRules: { isRequired: true, minLength: 1, maxLength: 2 },
            errorMessages: {
                isRequired: 'Please enter a valid month.',
                minLength: 'Month must be at least 1 digit',
                maxLength: 'Month must be less than or equal to 2 digits'
            }
        },
        daySelect: {
            name: 'daySelect',
            label: 'Day',
            validationRules: {
                isRequired: true,
                minLength: 1,
                maxLength: 2,
                fieldRegex: '^(0?[1-9]|[12][0-9]|3[01])$'
            },
            errorMessages: {
                isRequired: 'Please enter a valid day.',
                minLength: 'Day must be at least 1 digit',
                maxLength: 'Day must be less than or equal to 2 digits',
                fieldRegex: 'Please enter a valid day.'
            }
        },
        yearSelect: {
            name: 'yearSelect',
            label: 'Year',
            validationRules: {
                isRequired: true,
                minLength: 4,
                maxLength: 4,
                fieldRegex: '^[0-9]+$'
            },
            errorMessages: {
                isRequired: 'Please enter a valid year.',
                minLength: 'Year must be at least 4 digits',
                maxLength: 'Year must be less than or equal to 4 digits',
                fieldRegex: 'Please enter a valid year.'
            }
        },
        birthDate: {
            name: 'birthDate',
            validationRules: {
                validValues: null,
                isRequired: true,
                minLength: 10,
                maxLength: 10,
                fieldRegex: '^(?:(0[1-9]|1[012])[\\/](0[1-9]|[12][0-9]|3[01])[\\/][0-9]{4})$'
            }
        }
    },
    errorMessages: {
        isRequired: 'Please enter a valid date of birth.',
        validationText: 'Date of birth entered is not valid.',
        underEighteenError: 'Minor owner must be under 18 years old to open an account.',
        eighteenOrAboveError: "Account owner must be at least 18 years old to open an account. If you're not at least 18, please exit this application.",
        isMinor: 'Minor owner must be under 18 years old to open an account.',
        isMajor: "Account owner must be at least 18 years old to open an account. If you're not at least 18, please exit this application.",
        ageRange: 'Date provided is not in the age range for this transaction.',
        maxDateError: 'Please select a valid date.',
        maxDate: 'Please select a valid date.',
        minDate: 'Please select a valid date.'
    },
    defaultSelectLabel: 'Month'
};
const apexEmail = {
    fields: {
        primaryEmail: {
            name: 'primaryEmail',
            label: 'Primary Email address',
            errorMessages: {
                isRequired: 'Provide an email address.',
                maxLength: 'Email address has too many characters.',
                minLength: 'Email address has too few characters.',
                fieldRegex: 'Email address format is incorrect.'
            },
            supplemental: 'You can update your email address on your Profile once your account is open.',
            validationRules: {
                validValues: null,
                isRequired: true,
                minLength: 6,
                maxLength: 250,
                fieldRegex: "^([a-zA-Z0-9_\\-\\.\\']+)@([a-zA-Z0-9_\\-\\.]+)\\.([a-zA-Z]{2,5})$"
            }
        },
        secondaryEmail: {
            name: 'secondaryEmail',
            label: 'Secondary Email address',
            errorMessages: {
                isRequired: 'Provide an email address.',
                maxLength: 'Email address has too many characters.',
                minLength: 'Email address has too few characters.',
                fieldRegex: 'Email address format is incorrect.'
            },
            supplemental: 'You can update your email address on your Profile once your account is open.',
            validationRules: {
                validValues: null,
                isRequired: false,
                minLength: 1,
                maxLength: 250,
                fieldRegex: "^([a-zA-Z0-9_\\-\\.\\']+)@([a-zA-Z0-9_\\-\\.]+)\\.([a-zA-Z]{2,5})$"
            }
        },
        undefined: {}
    }
};
const apexFullName = {
    groupLabel: 'Full name',
    fields: {
        firstName: {
            name: 'firstName',
            label: 'First name',
            errorMessages: {
                isRequired: 'Provide a first name.',
                maxLength: 'First name has too many characters.',
                minLength: 'First name must be more than an initial.',
                fieldRegex: 'First name contains invalid characters.'
            },
            validationRules: {
                validValues: null,
                isRequired: true,
                minLength: 2,
                maxLength: 25,
                fieldRegex: "^[a-zA-Z0-9 '.,-]*$"
            }
        },
        middleName: {
            name: 'middleName',
            label: 'Middle name',
            errorMessages: {
                isRequired: 'Provide a middle name.',
                maxLength: 'Middle name has too many characters.',
                fieldRegex: 'Middle name contains invalid characters.'
            },
            validationRules: {
                validValues: null,
                isRequired: false,
                minLength: 0,
                maxLength: 10,
                fieldRegex: "^[a-zA-Z0-9 '.,-]*$"
            }
        },
        lastName: {
            name: 'lastName',
            label: 'Last name',
            errorMessages: {
                isRequired: 'Provide a last name.',
                maxLength: 'Last name has too many characters.',
                minLength: 'Last name must be more than an initial.',
                fieldRegex: 'Last name contains invalid characters.'
            },
            validationRules: {
                validValues: null,
                isRequired: true,
                minLength: 2,
                maxLength: 30,
                fieldRegex: "^[a-zA-Z0-9 '.,-]*$"
            }
        },
        undefined: {}
    }
};
const apexOptionsList = {
    optionsList: {
        label: '',
        errorMessages: {
            isRequired: 'An option must be selected.',
            allRequired: 'All options must be selected.'
        },
        multiSelectAll: "Multi-select options list with supplemental text and 'Select All' enabled.",
        multiSelect: 'Multi-select options list read only.',
        singleSelectAll: "Single-select options list with supplemental text and 'Select All' enabled.",
        singleSelect: 'Single-select options list read only.'
    }
};
const apexPhone = {
    fields: {
        telephoneNumber: {
            name: 'telephoneNumber',
            label: 'Phone number',
            errorMessages: {
                isRequired: 'Provide a phone number.',
                maxLength: 'Phone number has too many characters.',
                minLength: 'Phone number has too few characters.',
                fieldRegex: 'Phone number contains invalid characters.'
            },
            validationRules: {
                validValues: null,
                isRequired: true,
                minLength: 14,
                maxLength: 14,
                fieldRegex: '^[0-9-() ]*$'
            }
        },
        internationalPhoneNumber: {
            name: 'internationalPhoneNumber',
            label: 'Phone number',
            errorMessages: {
                isRequired: 'Provide a phone number.',
                maxLength: 'Phone number has too many characters.',
                minLength: 'Phone number has too few characters.',
                fieldRegex: 'Phone number contains invalid characters.'
            },
            validationRules: {
                validValues: null,
                isRequired: true,
                minLength: 4,
                maxLength: 11,
                fieldRegex: '^[0-9]*$'
            }
        },
        countryCode: {
            name: 'countryCode',
            label: 'Country Code',
            errorMessages: {
                isRequired: 'Provide a country code.',
                maxLength: 'Country code has too many characters.',
                minLength: 'Country code has too few characters.',
                fieldRegex: 'Country code contains invalid characters.'
            },
            validationRules: {
                validValues: null,
                isRequired: true,
                minLength: 1,
                maxLength: 4,
                fieldRegex: '^[0-9]*$'
            }
        },
        undefined: {}
    }
};
const apexRating = {
    fields: { rating: { name: 'rating', label: 'Rating' } }
};
const apexSelect = {
    fields: {
        select: {
            name: 'select',
            label: 'Select an option',
            errorMessages: { isRequired: 'An option must be selected.' },
            supplemental: 'Read-only example (linked to selection above)'
        }
    }
};
const apexSsn = {
    fields: {
        taxReportingId: {
            name: 'taxReportingId',
            label: 'Social Security number',
            errorMessages: {
                isRequired: 'Provide a Social Security number.',
                maxLength: 'Social Security number has too many characters.',
                minLength: 'Social Security number has too few characters.',
                fieldRegex: 'This Social Security number is invalid.'
            },
            validationRules: {
                validValues: null,
                isRequired: true,
                minLength: 9,
                maxLength: 9,
                fieldRegex: '^(?!123456789)(?!(.)\\1+$)(\\d)*$'
            }
        }
    }
};
const apexTextInput = {
    fields: {
        textInput: {
            name: 'textInput',
            label: 'Text input',
            errorMessages: {
                isRequired: 'Input required.',
                maxLength: 'Too few characters.',
                minLength: 'Too many characters.',
                fieldRegex: 'Input invalid.',
                range: 'The value may not contain numbers and must be between 2 and 10 characters long.'
            }
        }
    }
};
const apexToggle = {
    fields: {
        toggle: {
            name: 'toggle',
            label: 'Toggle',
            errorMessages: { isRequired: 'An option must be selected.' }
        }
    }
};
const apexUpload = {
    fields: {
        upload: {
            name: 'upload',
            errorMessages: {
                maxSize: 'Field size must be less than (number).',
                fileType: 'File uploaded is not an accepted file type.',
                fileExists: 'File already exists.',
                maxFiles: 'Sorry, you can only upload a maximum of (number) files.',
                maxTotalSize: 'Total size of all uploaded files must be less than (number). Try saving in a different format to reduce file size.'
            }
        }
    }
};

var commonContent = /*#__PURE__*/Object.freeze({
  __proto__: null,
  percentAllocation: percentAllocation,
  apexAddress: apexAddress,
  apexAutocomplete: apexAutocomplete,
  apexDate: apexDate,
  apexEmail: apexEmail,
  apexFullName: apexFullName,
  apexOptionsList: apexOptionsList,
  apexPhone: apexPhone,
  apexRating: apexRating,
  apexSelect: apexSelect,
  apexSsn: apexSsn,
  apexTextInput: apexTextInput,
  apexToggle: apexToggle,
  apexUpload: apexUpload
});

export { ApexEventBus, ApexFormatters, commonContent as CommonContent, apexCustomElement, deprecatePropertyForRemoval, deprecatePropertyTo };
//# sourceMappingURL=utils.js.map
